import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo, faLayerGroup, faSearch, faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTiktok,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useSocialWallCore } from "../../cores/socialWall";
import { socialWallApiService } from "../../cores/socialWall/services/api";
import {
  SocialWallAccount,
  SocialWallBoard,
  SocialWallExternalResult,
  SocialWallProvider,
  SocialWallStream,
} from "../../cores/socialWall/interfaces";
import {
  createBoard,
  createExternalStream,
  createStream,
  EXTERNAL_PROVIDERS,
  loadBoardsFromStorage,
  PROVIDER_META,
  saveBoardsToStorage,
} from "../component/socialWall/boardStorage";
import { SocialWallBoardModal } from "../component/socialWall/SocialWallBoardModal";
import { SocialWallColumn } from "../component/socialWall/SocialWallColumn";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import "../component/socialWall/SocialWall.scss";

const BRAND_ICONS: Record<string, typeof faFacebook> = {
  facebook: faFacebook,
  instagram: faInstagram,
  tiktok: faTiktok,
  linkedin_business: faLinkedin,
  youtube: faYoutube,
};

type PanelTab = "mine" | "competitors";

export default function SocialStreams() {
  const intl = useIntl();
  const { isDarkMode } = useGlobalCore();
  const { company, companyRole, isAdmin, isReseller } = useMeCore();
  const { goToSocial, goToBinding } = useRouterCore();
  const { accounts, loadingAccounts, loadAccounts } = useSocialWallCore();

  const [boards, setBoards] = useState<SocialWallBoard[]>([]);
  const [activeBoardId, setActiveBoardId] = useState<string>("");
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showBoardsDrawer, setShowBoardsDrawer] = useState(false);
  const [panelTab, setPanelTab] = useState<PanelTab>("mine");
  const [boardModalOpen, setBoardModalOpen] = useState(false);
  const [filterQ, setFilterQ] = useState("");
  const [mediaType, setMediaType] = useState("all");
  const [viewMode, setViewMode] = useState<"standard" | "compact">("standard");
  const [refreshKey, setRefreshKey] = useState(0);

  const [extProvider, setExtProvider] = useState<SocialWallProvider>("facebook");
  const [extQuery, setExtQuery] = useState("");
  const [extResults, setExtResults] = useState<SocialWallExternalResult[]>([]);
  const [extSearching, setExtSearching] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const draggingIdRef = React.useRef<string | null>(null);
  const dropTargetIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (
      !isAdmin &&
      !isReseller &&
      !companyRole?.owner &&
      !companyRole?.admin &&
      !companyRole?.social
    ) {
      goToSocial();
    }
  }, [companyRole, goToSocial, isAdmin, isReseller]);

  useEffect(() => {
    if (!company?.id) return;
    let cancelled = false;

    (async () => {
      try {
        const list = await loadAccounts(company.id);
        if (cancelled) return;

        const defaultName = intl.formatMessage({
          id: "app.socialWall.defaultBoard",
        });
        const streamsFromAccounts = () =>
          list.map((a) => createStream(a.provider, a.name, a.avatarUrl));

        const stored = loadBoardsFromStorage(company.id);
        if (stored?.length) {
          const boardsWithDefaults = stored.map((board, index) => {
            if (index === 0 && !(board.streams || []).length && list.length) {
              return { ...board, streams: streamsFromAccounts() };
            }
            return board;
          });
          setBoards(boardsWithDefaults);
          setActiveBoardId(boardsWithDefaults[0].id);
          return;
        }

        const board = createBoard(defaultName, streamsFromAccounts());
        setBoards([board]);
        setActiveBoardId(board.id);
      } catch {
        if (cancelled) return;
        const defaultName = intl.formatMessage({
          id: "app.socialWall.defaultBoard",
        });
        const board = createBoard(defaultName, []);
        setBoards([board]);
        setActiveBoardId(board.id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company?.id, loadAccounts, intl]);

  useEffect(() => {
    if (!company?.id || !boards.length) return;
    saveBoardsToStorage(company.id, boards);
  }, [boards, company?.id]);

  const activeBoard = useMemo(
    () => boards.find((b) => b.id === activeBoardId) || boards[0],
    [boards, activeBoardId]
  );

  const updateActiveStreams = useCallback(
    (updater: (streams: SocialWallStream[]) => SocialWallStream[]) => {
      setBoards((prev) =>
        prev.map((b) =>
          b.id === activeBoard?.id
            ? { ...b, streams: updater(b.streams || []) }
            : b
        )
      );
    },
    [activeBoard?.id]
  );

  const addStreamFromAccount = (account: SocialWallAccount) => {
    updateActiveStreams((streams) => [
      ...streams,
      createStream(account.provider, account.name, account.avatarUrl),
    ]);
    setShowAddPanel(false);
    setShowBoardsDrawer(false);
  };

  const addExternalStream = (result: SocialWallExternalResult) => {
    updateActiveStreams((streams) => [...streams, createExternalStream(result)]);
    setShowAddPanel(false);
    setShowBoardsDrawer(false);
    setExtResults([]);
    setExtQuery("");
  };

  const removeStream = (streamId: string) => {
    updateActiveStreams((streams) => streams.filter((s) => s.id !== streamId));
  };

  const toggleMinimizeStream = (streamId: string) => {
    updateActiveStreams((streams) =>
      streams.map((s) =>
        s.id === streamId ? { ...s, minimized: !s.minimized } : s
      )
    );
  };

  const moveStream = (streamId: string, direction: "left" | "right") => {
    updateActiveStreams((streams) => {
      const index = streams.findIndex((s) => s.id === streamId);
      if (index < 0) return streams;
      const target = direction === "left" ? index - 1 : index + 1;
      if (target < 0 || target >= streams.length) return streams;
      const next = [...streams];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const reorderStreams = (fromId: string, toId: string) => {
    if (!fromId || !toId || fromId === toId) return;
    updateActiveStreams((streams) => {
      const fromIndex = streams.findIndex((s) => s.id === fromId);
      const toIndex = streams.findIndex((s) => s.id === toId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return streams;
      const next = [...streams];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };

  const createNewBoard = (name: string) => {
    const board = createBoard(name);
    setBoards((prev) => [...prev, board]);
    setActiveBoardId(board.id);
    setBoardModalOpen(false);
    setShowBoardsDrawer(false);
  };

  const seedStreamsFromAccounts = () => {
    if (!accounts.length) {
      goToBinding();
      return;
    }
    updateActiveStreams(() =>
      accounts.map((a) => createStream(a.provider, a.name, a.avatarUrl))
    );
  };

  const searchCompetitors = async () => {
    if (!company?.id || !extQuery.trim()) return;
    setExtSearching(true);
    setExtResults([]);
    try {
      const results = await socialWallApiService.searchExternal(company.id, {
        provider: extProvider,
        q: extQuery.trim(),
      });
      setExtResults(results);
      if (!results.length) {
        toast.info(intl.formatMessage({ id: "app.socialWall.competitorsEmpty" }));
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ||
        (e as { message?: string })?.message ||
        intl.formatMessage({ id: "app.socialWall.competitorsEmpty" });
      toast.error(String(msg));
    } finally {
      setExtSearching(false);
    }
  };

  if (!company?.id) return null;

  return (
    <Layout>
      <Header2 title={intl.formatMessage({ id: "app.socialWall.title" })} />
      <Content>
        <div
          className={`social-wall-page${
            isDarkMode ? " social-wall-page--dark" : ""
          }`}
        >
          <div className="sw-layout">
            {(showBoardsDrawer || showAddPanel) && (
              <button
                type="button"
                className="sw-drawer-backdrop"
                aria-label={intl.formatMessage({ id: "app.socialWall.close" })}
                onClick={() => {
                  setShowBoardsDrawer(false);
                  setShowAddPanel(false);
                }}
              />
            )}

            <aside
              className={`sw-boards${
                showBoardsDrawer ? " sw-boards--open" : ""
              }`}
            >
              <div className="sw-boards__mobile-head">
                <strong>
                  {intl.formatMessage({ id: "app.socialWall.yourBoards" })}
                </strong>
                <button
                  type="button"
                  className="sw-icon-btn"
                  onClick={() => setShowBoardsDrawer(false)}
                  aria-label={intl.formatMessage({ id: "app.socialWall.close" })}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="sw-boards__label">
                {intl.formatMessage({ id: "app.socialWall.yourBoards" })}
              </div>
              {boards.map((board) => (
                <button
                  key={board.id}
                  type="button"
                  className={`sw-board-item${
                    board.id === activeBoard?.id ? " sw-board-item--active" : ""
                  }`}
                  onClick={() => {
                    setActiveBoardId(board.id);
                    setShowBoardsDrawer(false);
                  }}
                >
                  {board.name}
                </button>
              ))}
              <button
                type="button"
                className="sw-boards__new"
                onClick={() => setBoardModalOpen(true)}
              >
                + {intl.formatMessage({ id: "app.socialWall.newBoard" })}
              </button>

              <div className="sw-boards__label" style={{ marginTop: 18 }}>
                {intl.formatMessage({ id: "app.socialWall.networks" })}
              </div>
              {loadingAccounts ? (
                <div className="sw-empty" style={{ padding: 8 }}>
                  …
                </div>
              ) : accounts.length ? (
                accounts.map((a) => (
                  <button
                    key={`${a.provider}-${a.accountId}`}
                    type="button"
                    className="sw-board-item"
                    onClick={() => addStreamFromAccount(a)}
                    title={intl.formatMessage({
                      id: "app.socialWall.addStreamFromAccount",
                    })}
                  >
                    <FontAwesomeIcon
                      icon={BRAND_ICONS[a.provider] || faFacebook}
                      style={{
                        marginRight: 8,
                        color: PROVIDER_META[a.provider]?.color,
                      }}
                    />
                    {a.name}
                  </button>
                ))
              ) : (
                <button
                  type="button"
                  className="sw-boards__new"
                  onClick={goToBinding}
                >
                  {intl.formatMessage({ id: "app.socialWall.connectAccounts" })}
                </button>
              )}
            </aside>

            <div className="sw-main">
              <div className="sw-toolbar">
                <div className="sw-toolbar__top">
                  <button
                    type="button"
                    className="sw-btn sw-btn--ghost sw-toolbar__boards-btn"
                    onClick={() => setShowBoardsDrawer(true)}
                    aria-label={intl.formatMessage({
                      id: "app.socialWall.openBoards",
                    })}
                  >
                    <FontAwesomeIcon icon={faBars} />
                    <span>
                      {intl.formatMessage({ id: "app.socialWall.openBoards" })}
                    </span>
                  </button>
                  <div className="sw-toolbar__title">
                    {activeBoard?.name ||
                      intl.formatMessage({ id: "app.socialWall.title" })}
                  </div>
                </div>
                <div className="sw-toolbar__actions">
                  <input
                    className="sw-input"
                    placeholder={intl.formatMessage({
                      id: "app.socialWall.filterPlaceholder",
                    })}
                    value={filterQ}
                    onChange={(e) => setFilterQ(e.target.value)}
                  />
                  <select
                    className="sw-select"
                    value={mediaType}
                    onChange={(e) => setMediaType(e.target.value)}
                  >
                    <option value="all">
                      {intl.formatMessage({ id: "app.socialWall.filterAll" })}
                    </option>
                    <option value="image">
                      {intl.formatMessage({ id: "app.socialWall.filterImage" })}
                    </option>
                    <option value="video">
                      {intl.formatMessage({ id: "app.socialWall.filterVideo" })}
                    </option>
                    <option value="text">
                      {intl.formatMessage({ id: "app.socialWall.filterText" })}
                    </option>
                  </select>
                  <select
                    className="sw-select"
                    value={viewMode}
                    onChange={(e) =>
                      setViewMode(e.target.value as "standard" | "compact")
                    }
                  >
                    <option value="standard">
                      {intl.formatMessage({
                        id: "app.socialWall.viewStandard",
                      })}
                    </option>
                    <option value="compact">
                      {intl.formatMessage({
                        id: "app.socialWall.viewCompact",
                      })}
                    </option>
                  </select>
                  <button
                    type="button"
                    className="sw-btn"
                    onClick={() => setRefreshKey((k) => k + 1)}
                    title={intl.formatMessage({
                      id: "app.socialWall.refreshAll",
                    })}
                  >
                    <FontAwesomeIcon icon={faRedo} />
                  </button>
                  <button
                    type="button"
                    className="sw-btn sw-btn--primary"
                    onClick={() => {
                      setPanelTab("mine");
                      setShowBoardsDrawer(false);
                      setShowAddPanel(true);
                    }}
                  >
                    + {intl.formatMessage({ id: "app.socialWall.addStream" })}
                  </button>
                </div>
              </div>

              {!activeBoard?.streams?.length ? (
                <div className="sw-hint">
                  <div className="sw-hint__icon" aria-hidden>
                    <FontAwesomeIcon icon={faLayerGroup} />
                  </div>
                  <h2>
                    {intl.formatMessage({ id: "app.socialWall.emptyTitle" })}
                  </h2>
                  <p>
                    {intl.formatMessage({ id: "app.socialWall.emptyBody" })}
                  </p>
                  <button
                    type="button"
                    className="sw-btn sw-btn--primary"
                    onClick={seedStreamsFromAccounts}
                  >
                    {accounts.length
                      ? intl.formatMessage({
                          id: "app.socialWall.addAllConnected",
                        })
                      : intl.formatMessage({
                          id: "app.socialWall.connectAccounts",
                        })}
                  </button>
                </div>
              ) : (
                <div className="sw-streams" key={refreshKey}>
                  {activeBoard.streams.map((stream, index) => (
                    <SocialWallColumn
                      key={`${stream.id}-${refreshKey}`}
                      companyId={company.id}
                      stream={stream}
                      compact={viewMode === "compact"}
                      filterQ={filterQ}
                      mediaType={mediaType}
                      canMoveLeft={index > 0}
                      canMoveRight={index < activeBoard.streams.length - 1}
                      onRemove={removeStream}
                      onToggleMinimize={toggleMinimizeStream}
                      onMove={moveStream}
                      onDragStart={(id) => {
                        draggingIdRef.current = id;
                        dropTargetIdRef.current = null;
                        setDraggingId(id);
                        setDropTargetId(null);
                      }}
                      onDragOver={(id) => {
                        if (
                          draggingIdRef.current &&
                          id !== draggingIdRef.current
                        ) {
                          dropTargetIdRef.current = id;
                          setDropTargetId(id);
                        }
                      }}
                      onDragEnd={() => {
                        const from = draggingIdRef.current;
                        const to = dropTargetIdRef.current;
                        if (from && to) {
                          reorderStreams(from, to);
                        }
                        draggingIdRef.current = null;
                        dropTargetIdRef.current = null;
                        setDraggingId(null);
                        setDropTargetId(null);
                      }}
                      isDragging={draggingId === stream.id}
                      isDropTarget={
                        dropTargetId === stream.id && draggingId !== stream.id
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {showAddPanel ? (
              <aside className="sw-add-panel sw-add-panel--open">
                <div className="sw-add-panel__mobile-head">
                  <h3>
                    {intl.formatMessage({ id: "app.socialWall.addStream" })}
                  </h3>
                  <button
                    type="button"
                    className="sw-icon-btn"
                    onClick={() => setShowAddPanel(false)}
                    aria-label={intl.formatMessage({ id: "app.socialWall.close" })}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <h3 className="sw-add-panel__desktop-title">
                  {intl.formatMessage({ id: "app.socialWall.addStream" })}
                </h3>

                <div className="sw-add-tabs">
                  <button
                    type="button"
                    className={`sw-add-tab${
                      panelTab === "mine" ? " sw-add-tab--active" : ""
                    }`}
                    onClick={() => setPanelTab("mine")}
                  >
                    {intl.formatMessage({ id: "app.socialWall.myChannels" })}
                  </button>
                  <button
                    type="button"
                    className={`sw-add-tab${
                      panelTab === "competitors" ? " sw-add-tab--active" : ""
                    }`}
                    onClick={() => setPanelTab("competitors")}
                  >
                    {intl.formatMessage({ id: "app.socialWall.competitors" })}
                  </button>
                </div>

                {panelTab === "mine" ? (
                  !accounts.length ? (
                    <div className="sw-empty">
                      <p>
                        {intl.formatMessage({
                          id: "app.socialWall.noAccounts",
                        })}
                      </p>
                      <button
                        type="button"
                        className="sw-btn sw-btn--primary"
                        onClick={goToBinding}
                      >
                        {intl.formatMessage({
                          id: "app.socialWall.connectAccounts",
                        })}
                      </button>
                    </div>
                  ) : (
                    <div className="sw-add-list">
                      {accounts.map((a) => (
                        <button
                          key={`${a.provider}-${a.accountId}`}
                          type="button"
                          className="sw-add-item"
                          onClick={() => addStreamFromAccount(a)}
                        >
                          {a.avatarUrl ? (
                            <img src={a.avatarUrl} alt="" />
                          ) : (
                            <FontAwesomeIcon
                              icon={BRAND_ICONS[a.provider] || faFacebook}
                              size="lg"
                              style={{
                                color: PROVIDER_META[a.provider]?.color,
                                width: 36,
                              }}
                            />
                          )}
                          <span className="sw-add-item__text">
                            <strong>{a.name}</strong>
                            <small>
                              {PROVIDER_META[a.provider]?.label || a.provider} ·{" "}
                              {a.provider === "youtube" || a.provider === "tiktok"
                                ? intl.formatMessage({
                                    id: "app.socialWall.myVideos",
                                  })
                                : intl.formatMessage({
                                    id: "app.socialWall.myPosts",
                                  })}
                            </small>
                          </span>
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  <div className="sw-competitor">
                    <p className="sw-competitor__hint">
                      {intl.formatMessage({
                        id: "app.socialWall.competitorsHint",
                      })}
                    </p>
                    <select
                      className="sw-select sw-competitor__provider"
                      value={extProvider}
                      onChange={(e) => {
                        setExtProvider(e.target.value as SocialWallProvider);
                        setExtResults([]);
                      }}
                    >
                      {EXTERNAL_PROVIDERS.map((p) => (
                        <option key={p} value={p}>
                          {PROVIDER_META[p]?.label || p}
                        </option>
                      ))}
                    </select>
                    <div className="sw-competitor__search">
                      <input
                        className="sw-input"
                        value={extQuery}
                        onChange={(e) => setExtQuery(e.target.value)}
                        placeholder={intl.formatMessage({
                          id: "app.socialWall.competitorsSearchPlaceholder",
                        })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void searchCompetitors();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="sw-btn sw-btn--primary"
                        onClick={() => void searchCompetitors()}
                        disabled={extSearching || !extQuery.trim()}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                    <div className="sw-add-list" style={{ marginTop: 12 }}>
                      {extResults.map((r) => (
                        <button
                          key={`${r.provider}-${r.accountId}`}
                          type="button"
                          className="sw-add-item"
                          onClick={() => addExternalStream(r)}
                        >
                          {r.avatarUrl ? (
                            <img src={r.avatarUrl} alt="" />
                          ) : (
                            <FontAwesomeIcon
                              icon={BRAND_ICONS[r.provider] || faFacebook}
                              size="lg"
                              style={{
                                color: PROVIDER_META[r.provider]?.color,
                                width: 36,
                              }}
                            />
                          )}
                          <span className="sw-add-item__text">
                            <strong>{r.name}</strong>
                            <small>
                              {PROVIDER_META[r.provider]?.label || r.provider}
                              {r.username ? ` · @${r.username}` : ""} ·{" "}
                              {intl.formatMessage({
                                id: "app.socialWall.competitorsBadge",
                              })}
                            </small>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="sw-btn"
                  style={{ marginTop: 16, width: "100%" }}
                  onClick={() => setShowAddPanel(false)}
                >
                  {intl.formatMessage({ id: "app.socialWall.close" })}
                </button>
              </aside>
            ) : null}
          </div>
        </div>
      </Content>

      <SocialWallBoardModal
        isOpen={boardModalOpen}
        onCancel={() => setBoardModalOpen(false)}
        onConfirm={createNewBoard}
      />
    </Layout>
  );
}
